import { Component, ElementRef, inject, OnInit, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../core/services/auth.service';
import { GoogleIdentityService } from '../../core/services/google-identity.service';
import { Constante } from '../../constante';

@Component({
    selector: 'app-login-page',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterLink],
    template: `
    <div class="min-h-screen flex flex-col" style="background-color: #fff5f7;">
      <header class="py-4 px-6 flex items-center justify-between text-white" style="background-color: var(--bg-main);">
        <a routerLink="/" class="flex items-center gap-3">
          <img [src]="logoUrl" alt="Logo" class="h-10 w-auto object-contain brightness-0 invert" />
        </a>
        <a routerLink="/" class="text-sm font-semibold hover:underline">Volver a la tienda</a>
      </header>

      <div class="flex-1 flex items-center justify-center px-4 py-10">
        <div class="w-full max-w-md bg-white rounded-[2rem] shadow-xl p-8">
          <h1 class="text-2xl font-black text-center text-gray-800 mb-1">{{ modoRegistro() ? 'Crear cuenta' : 'Iniciar sesión' }}</h1>
          <p class="text-center text-sm text-gray-500 mb-6">
            {{ modoRegistro() ? 'Regístrate para comprar o gestionar pedidos' : 'Personal de la empresa y clientes' }}
          </p>

          <button type="button" [disabled]="loading()" (click)="onGoogle()" class="w-full py-3 rounded-2xl border border-gray-200 font-bold text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2 disabled:opacity-60">
            <svg class="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#EA4335" d="M12 10.2v3.6h5.1c-.2 1.2-1.5 3.6-5.1 3.6-3.1 0-5.6-2.5-5.6-5.6S8.9 6.2 12 6.2c1.7 0 2.9.7 3.6 1.3l2.4-2.4C16.5 3.7 14.5 2.8 12 2.8 6.9 2.8 2.8 6.9 2.8 12S6.9 21.2 12 21.2c5.2 0 8.6-3.6 8.6-8.7 0-.6 0-1-.1-1.5H12z"/>
            </svg>
            Continuar con Google
          </button>
          <div class="sr-only" aria-hidden="true">
            <div #googleHost></div>
          </div>

          <div class="flex items-center gap-3 my-5">
            <span class="flex-1 h-px bg-gray-200"></span>
            <span class="text-xs uppercase tracking-widest text-gray-400">o</span>
            <span class="flex-1 h-px bg-gray-200"></span>
          </div>

          <form class="flex flex-col gap-3" (ngSubmit)="submit()">
            <ng-container *ngIf="modoRegistro()">
              <input [(ngModel)]="nombre" name="nombre" required placeholder="Nombre" class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-300" />
              <input [(ngModel)]="apellidos" name="apellidos" required placeholder="Apellidos" class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-300" />
            </ng-container>
            <input [(ngModel)]="correo" name="correo" type="email" required placeholder="Correo electrónico" class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-300" />
            <input [(ngModel)]="password" name="password" type="password" required placeholder="Contraseña" class="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-rose-300" />

            <p *ngIf="error()" class="text-xs text-rose-500 font-semibold">{{ error() }}</p>

            <button type="submit" [disabled]="loading()" class="w-full py-3 rounded-2xl text-white font-bold text-sm uppercase tracking-wide disabled:opacity-60" style="background-color: var(--bg-main);">
              {{ loading() ? 'Espera...' : (modoRegistro() ? 'Registrarme' : 'Iniciar sesión') }}
            </button>
          </form>

          <button type="button" (click)="toggleModo()" class="mt-5 w-full text-sm text-gray-500 hover:text-gray-800">
            {{ modoRegistro() ? '¿Ya tienes cuenta? Inicia sesión' : '¿No tienes cuenta? Regístrate' }}
          </button>
        </div>
      </div>
    </div>
    `
})
export class LoginPageComponent implements OnInit {
    private auth = inject(AuthService);
    private google = inject(GoogleIdentityService);
    private router = inject(Router);
    private googleHost = viewChild<ElementRef<HTMLElement>>('googleHost');

    logoUrl = 'https://undetalle.com.pe/wp-content/uploads/2025/11/2212f1a8128bbb7b77ac4c41ea233e62fcaf5bd8.png';
    modoRegistro = signal(false);
    loading = signal(false);
    error = signal('');

    correo = '';
    password = '';
    nombre = '';
    apellidos = '';

    ngOnInit(): void {
        if (this.router.url.includes('register')) {
            this.modoRegistro.set(true);
        }
        if (this.auth.isAuthenticated()) {
            this.router.navigate([this.auth.isAdmin() ? Constante.URL_ADMIN : Constante.URL_HOME]);
            return;
        }
        this.google.warmUp().catch(() => undefined);
    }

    toggleModo() {
        this.modoRegistro.set(!this.modoRegistro());
        this.error.set('');
    }

    submit() {
        this.error.set('');
        this.loading.set(true);
        const req$ = this.modoRegistro()
            ? this.auth.register({
                correo: this.correo,
                password: this.password,
                nombre: this.nombre,
                apellidos: this.apellidos,
                nombreVisible: `${this.nombre} ${this.apellidos}`.trim()
            })
            : this.auth.login({ correo: this.correo, password: this.password });

        req$.subscribe({
            next: () => {
                this.loading.set(false);
                this.router.navigate([this.auth.isAdmin() ? Constante.URL_ADMIN : Constante.URL_HOME]);
            },
            error: (err) => {
                this.loading.set(false);
                this.error.set(this.readError(err));
            }
        });
    }

    onGoogle() {
        this.error.set('');
        this.loading.set(true);
        const start = () => {
            this.google.requestCredential(this.googleHost()?.nativeElement).then((idToken) => {
                this.auth.loginGoogle({ idToken }).subscribe({
                    next: () => {
                        this.loading.set(false);
                        this.router.navigate([this.auth.isAdmin() ? Constante.URL_ADMIN : Constante.URL_HOME]);
                    },
                    error: (err) => {
                        this.loading.set(false);
                        this.error.set(this.readError(err));
                    }
                });
            }).catch((err) => {
                this.loading.set(false);
                this.error.set(this.readError(err));
            });
        };

        if (this.google.isReady()) {
            start();
            return;
        }

        this.google.warmUp().then(start).catch((err) => {
            this.loading.set(false);
            this.error.set(this.readError(err));
        });
    }

    private readError(err: unknown): string {
        const e = err as { error?: { errors?: { message?: string }[]; message?: string }; message?: string };
        return e?.error?.errors?.[0]?.message || e?.error?.message || e?.message || 'No se pudo iniciar sesión.';
    }
}
